import {
  ARM,
  INSPECTION_PLATE,
  INSPECTION_X,
  PICK_X,
  ROBOT_OFFSET_X,
  SCENE,
} from './railLayout';
import { SIGNAL_DASH, SOUND_ARC_RADII, soundKeyframes } from './railSound';
import {
  BOX_COUNT,
  DEFECT_EVENTS,
  LIFT_OFFSET,
  LOOP_SECONDS,
  PICK,
  TRAVEL_FROM,
  TRAVEL_TO,
  VERDICT_SECONDS,
  boxPassesAt,
  inspectionWindow,
  localTimeAt,
} from './railTiming';
import { SIDE_FACES, turnKeyframes } from './railTurn';

const pct = (seconds: number, loop = LOOP_SECONDS) =>
  `${((seconds / loop) * 100).toFixed(3)}%`;

function assertInsideLoop(what: string, seconds: number) {
  if (seconds >= LOOP_SECONDS) {
    throw new Error(`${what} runs past the loop (${seconds.toFixed(2)}s)`);
  }
}

/** Stroke width the rest of the drawing uses, in scene units. */
const STROKE_WIDTH = 1.5;

/**
 * Every box rolls in at one speed, stands on the plate for the hold, and
 * rolls out at the same speed. On the plate it is carried up by the lift as
 * the disc rises and set down again as it settles, by exactly the disc's
 * travel, so it stays on the disc. The stop is in the box's own time, so
 * one keyframe serves every box and the negative delays stagger the stops.
 */
function travelKeyframes(): string {
  const { start, up, down, end } = inspectionWindow();
  const raised = `translate(${INSPECTION_X}px, ${-INSPECTION_PLATE.lift}px)`;

  return `
  @keyframes prl-travel {
    0% { transform: translate(${TRAVEL_FROM}px, 0); }
    ${pct(start)} { transform: translate(${INSPECTION_X}px, 0); }
    ${pct(up)} { transform: ${raised}; }
    ${pct(down)} { transform: ${raised}; }
    ${pct(end)} { transform: translate(${INSPECTION_X}px, 0); }
    100% { transform: translate(${TRAVEL_TO}px, 0); }
  }`;
}

/**
 * A defect box travels like every other box, plate stop and lift included,
 * until it is under the gripper, stops there, and is then carried up by
 * exactly the distance the gripper rises, so it reads as held rather than
 * floating. It fades once it is clear of the line: that is what taking it
 * off the line looks like from here. These stops are in the box's own time,
 * so one keyframe serves both defects.
 */
function defectTravel(): string {
  const { start, up, down, end } = inspectionWindow();
  const arrive = localTimeAt(PICK_X);
  const gripped = arrive + PICK.hold;
  const lifted = gripped + PICK.lift;
  const gone = lifted + PICK.release;
  assertInsideLoop('the defect pick', gone);
  const raised = `translate(${INSPECTION_X}px, ${-INSPECTION_PLATE.lift}px)`;
  const held = `${(PICK_X + LIFT_OFFSET.x).toFixed(1)}px`;

  return `
  @keyframes prl-travel-defect {
    0% { transform: translate(${TRAVEL_FROM}px, 0); opacity: 1; }
    ${pct(start)} { transform: translate(${INSPECTION_X}px, 0); opacity: 1; }
    ${pct(up)} { transform: ${raised}; opacity: 1; }
    ${pct(down)} { transform: ${raised}; opacity: 1; }
    ${pct(end)} { transform: translate(${INSPECTION_X}px, 0); opacity: 1; }
    ${pct(arrive)} { transform: translate(${PICK_X}px, 0); opacity: 1; }
    ${pct(gripped)} { transform: translate(${PICK_X}px, 0); opacity: 1; }
    ${pct(lifted)} { transform: translate(${held}, ${LIFT_OFFSET.y.toFixed(1)}px); opacity: 1; }
    ${pct(gone - 0.2)} { transform: translate(${held}, ${LIFT_OFFSET.y.toFixed(1)}px); opacity: 1; }
    ${pct(gone)}, 100% { transform: translate(${held}, ${(LIFT_OFFSET.y - 10).toFixed(1)}px); opacity: 0; }
  }`;
}

/** A moment in the shared loop and how far up the disc is, 0 flush to 1 raised. */
interface LiftStop {
  at: number;
  value: number;
}

/**
 * Stops that may run past the loop's end, folded back into it: a stop past
 * the end is moved to the start, and where a segment crosses the end its
 * value there is pinned on both sides, so the loop restarts mid-movement
 * without a jump. Whatever crosses, the two ends always agree.
 */
function foldIntoLoop(stops: ReadonlyArray<LiftStop>): Array<LiftStop> {
  const sorted = [...stops].sort((left, right) => left.at - right.at);
  let atEnds = 0;
  for (let index = 0; index + 1 < sorted.length; index += 1) {
    const before = sorted[index];
    const after = sorted[index + 1];
    if (
      before !== undefined &&
      after !== undefined &&
      before.at < LOOP_SECONDS &&
      after.at > LOOP_SECONDS
    ) {
      const share = (LOOP_SECONDS - before.at) / (after.at - before.at);
      atEnds = before.value + (after.value - before.value) * share;
    }
  }

  return [
    ...sorted.map(({ at, value }) => ({
      at: at >= LOOP_SECONDS ? at - LOOP_SECONDS : at,
      value,
    })),
    { at: 0, value: atEnds },
    { at: LOOP_SECONDS, value: atEnds },
  ].sort((left, right) => left.at - right.at);
}

/**
 * The disc rising to meet each box as it arrives and settling once the box
 * is set down, in the shared loop's time: one lift per box, from the same
 * arrival times the boxes' own keyframes stop on. The disc's top moves up
 * by the lift; its wall is scaled up from nothing to full height about the
 * rim of the well, so it appears to stand up out of the belt.
 */
function plateKeyframes(): string {
  const { start, up, down, end } = inspectionWindow();
  const raw: Array<LiftStop> = [];
  for (let index = 0; index < BOX_COUNT; index += 1) {
    const arrival = boxPassesAt(index, INSPECTION_X);
    raw.push(
      { at: arrival, value: 0 },
      { at: arrival + (up - start), value: 1 },
      { at: arrival + (down - start), value: 1 },
      { at: arrival + (end - start), value: 0 },
    );
  }
  const stops = foldIntoLoop(raw);
  const top = stops.map(
    ({ at, value }) =>
      `${pct(at)} { transform: translateY(${(-INSPECTION_PLATE.lift * value).toFixed(2)}px); }`,
  );
  const wall = stops.map(
    ({ at, value }) => `${pct(at)} { transform: scaleY(${value.toFixed(3)}); }`,
  );

  return `
  @keyframes prl-plate-top { ${top.join(' ')} }
  @keyframes prl-plate-wall { ${wall.join(' ')} }`;
}

/**
 * The picker's joints. Between picks the arm hovers clear of the belt; as a
 * defect arrives it comes down onto the box, the gripper closes, the arm
 * raises the box off the line, and then it returns to hovering.
 */
function pickerKeyframes(): string {
  const arm: Array<string> = [];
  const grip: Array<string> = [];
  for (const { pickedAt } of DEFECT_EVENTS) {
    const start = pickedAt - PICK.descend;
    const gripped = pickedAt + PICK.hold;
    const lifted = gripped + PICK.lift;
    const home = lifted + PICK.release;
    assertInsideLoop('a pick cycle', home);
    arm.push(
      `${pct(start)} { transform: rotate(${ARM.restAngle}deg); }`,
      `${pct(pickedAt)} { transform: rotate(${ARM.grabAngle}deg); }`,
      `${pct(gripped)} { transform: rotate(${ARM.grabAngle}deg); }`,
      `${pct(lifted)} { transform: rotate(${ARM.liftAngle}deg); }`,
      `${pct(home)} { transform: rotate(${ARM.restAngle}deg); }`,
    );
    grip.push(
      `${pct(pickedAt)} { transform: scaleX(1); }`,
      `${pct(gripped)} { transform: scaleX(0.66); }`,
      `${pct(lifted)} { transform: scaleX(0.66); }`,
      `${pct(home)} { transform: scaleX(1); }`,
    );
  }

  return `
  @keyframes prl-arm { 0%, 100% { transform: rotate(${ARM.restAngle}deg); } ${arm.join(' ')} }
  @keyframes prl-grip { 0%, 100% { transform: scaleX(1); } ${grip.join(' ')} }`;
}

/**
 * The moments a verdict is on screen: from the judgment until the picker
 * has lifted the box, or until the verdict has had its own time, whichever
 * is later. Shared by the server's badge, its alert LED and the plate's
 * wash, so the three read as one event.
 */
function verdictSpans(): Array<{ from: number; to: number }> {
  return DEFECT_EVENTS.map(({ judgedAt, pickedAt }) => {
    const to = Math.max(
      judgedAt + VERDICT_SECONDS,
      pickedAt + PICK.hold + PICK.lift,
    );
    assertInsideLoop('a verdict', to);

    return { from: judgedAt, to };
  });
}

/** Keyframes that hold `on` through each verdict and `off` between them. */
function verdictToggle(name: string, off: number, on: number): string {
  const stops = verdictSpans().flatMap(({ from, to }) => [
    `${pct(from - 0.05)} { opacity: ${off}; }`,
    `${pct(from)} { opacity: ${on}; }`,
    `${pct(to)} { opacity: ${on}; }`,
    `${pct(to + 0.05)} { opacity: ${off}; }`,
  ]);

  return `@keyframes ${name} { 0%, 100% { opacity: ${off}; } ${stops.join(' ')} }`;
}

/**
 * The server's badge reads OK until a defect has been heard out, turns NG
 * as its turn completes, and holds until the picker has taken that box off
 * the line. Clearing it mid-pick would leave the arm acting on a verdict
 * the screen had already forgotten. The plate's red wash follows the same
 * clock, understated, so the judgment is seen where the unit stands as
 * well as on the screen.
 */
function verdictKeyframes(): string {
  return [
    verdictToggle('prl-verdict-ng', 0, 1),
    verdictToggle('prl-verdict-ok', 1, 0),
    verdictToggle('prl-zone-ng', 0, 0.2),
  ].join('\n  ');
}

/**
 * The faces of a unit, each on its own keyframes. The defect's faces run a
 * second animation for the shading; a single duration and delay applies to
 * both. Strokes are drawn at screen width so the affine turn cannot thin or
 * thicken them, and that width is derived from the frame's height so it
 * still matches the rest of the drawing at every breakpoint.
 */
function faceRules(): string {
  const names = SIDE_FACES.map(
    ({ id }) => `
  .prl-face-${id} { animation-name: prl-face-${id}; }
  .prl-box-defect .prl-face-${id} { animation-name: prl-face-${id}, prl-face-${id}-shade; }`,
  );

  return `
  .prl-face {
    transform-origin: 0 0;
    stroke-width: calc(100cqh * ${STROKE_WIDTH} / ${SCENE.height});
    animation-duration: ${LOOP_SECONDS}s;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    animation-delay: var(--prl-delay, 0s);
  }
  .prl-face-top { animation-name: prl-face-top; }${names.join('')}`;
}

/**
 * The station and the sound: the disc and its wall on the shared loop's
 * clock, the ripples on each box's own clock, and the signal run flowing
 * on the shared clock. The wall rests scaled flat, so with the animation
 * off it is not seen standing.
 */
function stationRules(): string {
  const ripples = SOUND_ARC_RADII.map(
    (_, index) => `
  .prl-sound-${index} { animation-name: prl-sound-${index}; }`,
  );

  return `
  .prl-plate-top { animation: prl-plate-top ${LOOP_SECONDS}s linear infinite; }
  .prl-plate-wall {
    transform: scaleY(0);
    transform-origin: ${INSPECTION_PLATE.x}px ${INSPECTION_PLATE.y}px;
    animation: prl-plate-wall ${LOOP_SECONDS}s linear infinite;
  }
  .prl-sound {
    animation-duration: ${LOOP_SECONDS}s;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    animation-delay: var(--prl-delay, 0s);
  }${ripples.join('')}
  .prl-signal-path {
    opacity: 0.55;
    stroke-dasharray: ${SIGNAL_DASH.on} ${SIGNAL_DASH.off};
    animation: prl-signal-flow ${LOOP_SECONDS}s linear infinite;
  }`;
}

/**
 * ### PRODUCTION_RAIL_STYLE
 *
 * Keyframes for the production-rail illustration, scoped by the `prl-`
 * prefix and shipped inside the SVG so the drawing carries its own motion.
 * Every animated class is switched off under reduced motion: the movement is
 * decoration, the drawing is the content. With the animation off, the belt
 * stands still with its boxes on it, the disc sits flush, the picker
 * rests, and the screen reads OK.
 */
export const PRODUCTION_RAIL_STYLE = `
  .prl-box {
    animation: prl-travel ${LOOP_SECONDS}s linear infinite;
    animation-delay: var(--prl-delay, 0s);
  }
  .prl-box-defect { animation-name: prl-travel-defect; }
  ${faceRules()}
  ${stationRules()}
  .prl-zone-ng { animation: prl-zone-ng ${LOOP_SECONDS}s linear infinite; }
  .prl-led { animation: prl-led 1.2s ease-in-out infinite; }
  .prl-arm { animation: prl-arm ${LOOP_SECONDS}s ease-in-out infinite; transform-origin: ${ARM.shoulder.x - ROBOT_OFFSET_X}px ${ARM.shoulder.y}px; }
  .prl-grip { animation: prl-grip ${LOOP_SECONDS}s ease-in-out infinite; transform-origin: ${ARM.gripAtRest.x - ROBOT_OFFSET_X}px ${ARM.gripAtRest.y}px; }
  .prl-verdict-ng { animation: prl-verdict-ng ${LOOP_SECONDS}s linear infinite; }
  .prl-verdict-ok { animation: prl-verdict-ok ${LOOP_SECONDS}s linear infinite; }
  .prl-led-alert { animation: prl-verdict-ng ${LOOP_SECONDS}s linear infinite; }
  ${travelKeyframes()}
  ${defectTravel()}
  ${pickerKeyframes()}
  ${verdictKeyframes()}
  ${turnKeyframes()}
  ${plateKeyframes()}
  ${soundKeyframes()}
  @keyframes prl-led {
    0%, 100% { opacity: 0.15; }
    50% { opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    .prl-box, .prl-face, .prl-sound, .prl-signal-path, .prl-plate-top,
    .prl-plate-wall, .prl-led, .prl-arm, .prl-grip,
    .prl-verdict-ng, .prl-verdict-ok, .prl-led-alert,
    .prl-zone-ng { animation: none; }
  }
`;
